import Phaser from 'phaser';
import { DENOMINATIONS_CENTS } from '../config.js';
import { createChipSprite } from './Chip.js';

interface RackEntry {
  container: Phaser.GameObjects.Container;
  glow: Phaser.GameObjects.Graphics;
  chip: Phaser.GameObjects.Sprite;
  denom: number;
  jigglePhase: number;
  affordable: boolean;
}

export class ChipRack extends Phaser.GameObjects.Container {
  private entries: RackEntry[] = [];
  private _selectedCents = 500;
  private bankrollCents = Number.POSITIVE_INFINITY;
  private flashTween: Phaser.Tweens.Tween | undefined = undefined;
  private jiggleTween: Phaser.Tweens.Tween | undefined = undefined;

  /**
   * Selected denomination in cents. Setting this also refreshes the visual
   * highlight — bridges that restore the persisted denom on entry need the
   * chip glow to follow the value, not just the engine state.
   */
  get selectedCents(): number { return this._selectedCents; }
  set selectedCents(cents: number) {
    if (this._selectedCents === cents) return;
    this._selectedCents = cents;
    if (this.entries.length > 0) this.refreshSelection();
  }

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    scale = 1,
    denominations: readonly number[] = DENOMINATIONS_CENTS,
  ) {
    super(scene, x, y);
    const r = 24 * scale;
    const spacing = 70 * scale;

    denominations.forEach((denom, i) => {
      const c = scene.add.container(i * spacing, 0);

      // Glow ring drawn behind the chip; only visible when selected.
      const glow = scene.add.graphics();
      glow.fillStyle(0xffe079, 0.45);
      glow.fillCircle(0, 0, r * 1.25);
      glow.setVisible(false);

      const chip = createChipSprite(scene, denom, r);

      c.add([glow, chip]);
      c.setSize(r * 2, r * 2);
      c.setInteractive({
        hitArea: new Phaser.Geom.Circle(r, r, r),
        hitAreaCallback: Phaser.Geom.Circle.Contains,
        useHandCursor: true,
      });
      c.on('pointerdown', () => {
        this.selectedCents = denom;
        this.refreshSelection();
        scene.events.emit('rack:select', denom);
      });

      this.entries.push({
        container: c,
        glow,
        chip,
        denom,
        jigglePhase: Math.random() * Math.PI * 2,
        affordable: true,
      });
      this.add(c);
    });

    this.refreshSelection();
    scene.add.existing(this);
    this.startIdleJiggle();
    this.once(Phaser.GameObjects.Events.DESTROY, () => this.stopIdleJiggle());
  }

  /**
   * Tap-invitation buzz on the rack chips, mirroring the dice idle jiggle but
   * dialed back to ~65% magnitude so the chips read as "tap me" without
   * competing with the dice for attention. Each chip carries its own random
   * phase so the rack doesn't move in lockstep.
   */
  private startIdleJiggle(): void {
    if (this.jiggleTween) return;
    this.jiggleTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 4000,
      repeat: -1,
      onUpdate: () => {
        const now = this.scene.time.now / 1000;
        for (const entry of this.entries) {
          if (!entry.affordable) continue;
          const t = now + entry.jigglePhase;
          const envelope = 0.7 + 0.3 * Math.sin(t * 1.3);
          const dx = (Math.sin(t * 14.3) * 0.6 + Math.sin(t * 27.1) * 0.3) * envelope;
          const dy = (Math.cos(t * 12.7) * 0.55 + Math.cos(t * 25.4) * 0.28) * envelope;
          const angDeg = (Math.sin(t * 16.2) * 1.1 + Math.sin(t * 9.4) * 0.5) * envelope;
          entry.chip.setRotation((angDeg * Math.PI) / 180);
          entry.chip.setPosition(dx, dy);
        }
      },
    });
  }

  private stopIdleJiggle(): void {
    this.jiggleTween?.stop();
    this.jiggleTween = undefined;
  }

  /**
   * Mute chips the player can't currently afford. The chips remain
   * selectable — when a placement attempt fails for insufficient funds,
   * `flashCannotAfford` is called instead to give visceral feedback.
   */
  setBankrollCents(cents: number) {
    this.bankrollCents = cents;
    for (const entry of this.entries) {
      const affordable = entry.denom <= cents;
      entry.affordable = affordable;
      entry.container.setAlpha(affordable ? 1 : 0.3);
      if (!affordable) {
        entry.chip.setRotation(0);
        entry.chip.setPosition(0, 0);
      }
    }
  }

  /**
   * Quick double-pulse of solid red on the currently-selected chip when a
   * bet is rejected for insufficient bankroll. We never fade through pink
   * back to neutral — that would briefly expose the chip's gold accents at
   * warm intermediate tones and read as a red→yellow flash.
   */
  flashCannotAfford() {
    const entry = this.entries.find((e) => e.denom === this.selectedCents);
    if (!entry) return;
    if (this.flashTween) this.flashTween.stop();
    const RED_TINT = 0xff2020;
    const PULSE_MS = 90;
    const restore = () => {
      entry.chip.clearTint();
      const affordable = entry.denom <= this.bankrollCents;
      entry.container.setAlpha(affordable ? 1 : 0.3);
      this.flashTween = undefined;
    };
    entry.container.setAlpha(1);
    entry.chip.setTint(RED_TINT);
    const state = { step: 0 };
    this.flashTween = this.scene.tweens.add({
      targets: state,
      step: 1,
      duration: PULSE_MS,
      yoyo: true,
      repeat: 2, // → on, off, on, off, on, off → ends back at "off"
      onYoyo: () => entry.chip.clearTint(),
      onRepeat: () => entry.chip.setTint(RED_TINT),
      onComplete: restore,
      onStop: restore,
    });
  }

  /**
   * World position of the currently-selected chip's center. Used by bridges
   * to spawn placement-fly chips from the rack chip the player tapped, so
   * the chip visibly travels from the rack onto the layout.
   *
   * Falls back to the rack's own (x, y) if no entry matches the selected
   * denomination (rack mid-rebuild during refreshChipRack). Callers always
   * receive a defined point — bet placements never silently skip the fly.
   */
  getSelectedChipAnchor(): { x: number; y: number } {
    const entry = this.entries.find((e) => e.denom === this.selectedCents);
    if (!entry) return { x: this.x, y: this.y };
    return { x: this.x + entry.container.x, y: this.y + entry.container.y };
  }

  /**
   * World position of the rack chip matching `cents`. Used for recall flies
   * (table → rack) so a $25 chip returns to the $25 slot, not whichever chip
   * is currently selected. Falls back to the selected anchor when the denom
   * isn't in the current rack (e.g. odds amounts that don't match a chip).
   */
  getChipAnchor(cents: number): { x: number; y: number } {
    const entry = this.entries.find((e) => e.denom === cents);
    if (!entry) return this.getSelectedChipAnchor();
    return { x: this.x + entry.container.x, y: this.y + entry.container.y };
  }

  private refreshSelection() {
    for (const entry of this.entries) {
      const selected = entry.denom === this.selectedCents;
      entry.container.setScale(selected ? 1.15 : 1);
      entry.glow.setVisible(selected);
    }
  }
}
